import {
  CONTROLLER_SELECTED_CLEAR,
  CONTROLLER_SELECTED_SET,
  CONTROLLERS_ADMIN_SET,
  CONTROLLER_ORDER_MOVE,
  CONTROLLERS_FETCH,
  CONTROLLERS_SET,
  MIDNIGHT_VALUES_FETCH,
  MIDNIGHT_VALUES_SET,
  MUTES_SET,
} from "./controllerActions";
import { SET_CONTROLLER_WATCHES } from "../settings/settingsActions";
import { PublicMapVisibilityParams } from "./controllerActionsTyped.ts";
import { MoveDirection, moveInList } from "./controllerSort";
import type {
  ControllerViewModel,
  MidnightValueViewModel,
} from "../api/models";

export type { MidnightValueViewModel as MidnightValue } from "../api/models";

export type ControllerState = {
  midnightValues: Record<
    string,
    {
      scales: Record<string, MidnightValueViewModel[]>;
      fetching: boolean;
    }
  >;
  list: Controller[];
  adminList: Controller[];
  watchedList: Controller[];
  mutes: {};
  selected: any;
  fetching: boolean;
};
/**
 * Wire shape from the backend (generated ControllerViewModel) plus
 * frontend-only augmentation: readOnly is stamped onto watched controllers
 * in SET_CONTROLLER_WATCHES, never sent by the API.
 */
export type Controller = ControllerViewModel & { readOnly?: boolean };

export type ControllerOrderMoveAction = {
  type: typeof CONTROLLER_ORDER_MOVE;
  controllerId: number;
  direction: MoveDirection;
  /**
   * Owned and watched stations are separate dashboard sections and are
   * renumbered independently — a shared station can never move above the last
   * owned one.
   */
  section: "owned" | "watched";
};

export const CONTROLLER_PUBLIC_MAP_VISIBILITY_SET =
  "CONTROLLER_PUBLIC_MAP_VISIBILITY_SET";
export type ControllerPublicMapVisibilitySetAction = {
  type: typeof CONTROLLER_PUBLIC_MAP_VISIBILITY_SET;
  controllerId: number;
  params: PublicMapVisibilityParams;
};

const defaultState: ControllerState = {
  list: [],
  adminList: [],
  watchedList: [],
  mutes: {},
  midnightValues: {},
  selected: null,
  fetching: false,
};
export default function (
  state: ControllerState = defaultState,
  action: any,
): ControllerState {
  switch (action.type) {
    case CONTROLLER_PUBLIC_MAP_VISIBILITY_SET: {
      const myAction = action as ControllerPublicMapVisibilitySetAction;

      function updateVisibility(
        list: Controller[],
        controllerId: number,
        params: PublicMapVisibilityParams,
      ) {
        return list.map((c) => {
          if (c.id === controllerId) {
            return {
              ...c,
              publicName: params.publicName,
              publicDescription: params.publicDescription,
            };
          }
          return c;
        });
      }

      return {
        ...state,
        list: updateVisibility(
          state.list,
          myAction.controllerId,
          myAction.params,
        ),
        adminList: updateVisibility(
          state.adminList,
          myAction.controllerId,
          myAction.params,
        ),
      };
    }
    case CONTROLLER_ORDER_MOVE: {
      const myAction = action as ControllerOrderMoveAction;
      // Optimistic: the tile moves immediately and the request follows. On
      // failure the action creator reloads, which snaps back to server truth.
      return myAction.section === "watched"
        ? {
            ...state,
            watchedList: moveInList(
              state.watchedList,
              myAction.controllerId,
              myAction.direction,
            ),
          }
        : {
            ...state,
            list: moveInList(
              state.list,
              myAction.controllerId,
              myAction.direction,
            ),
          };
    }
    case MIDNIGHT_VALUES_FETCH: {
      return {
        ...state,
        midnightValues: {
          ...state.midnightValues,
          [action.controllerId]: {
            ...state.midnightValues[action.controllerId],
            fetching: true,
          },
        },
      };
    }
    case MIDNIGHT_VALUES_SET: {
      return {
        ...state,
        midnightValues: {
          ...state.midnightValues,
          [action.controllerId]: {
            scales: action.scales,
            fetching: false,
          },
        },
      };
    }
    case SET_CONTROLLER_WATCHES:
      return {
        ...state,
        watchedList: action.watches.map((c: any) => ({ ...c, readOnly: true })),
      };
    case CONTROLLERS_FETCH:
      return {
        ...state,
        fetching: true,
      };
    case CONTROLLERS_SET:
      return {
        ...state,
        fetching: false,
        list: action.controllers,
      };
    case CONTROLLER_SELECTED_SET:
      return {
        ...state,
        fetching: false,
        selected: action.controller,
      };
    case CONTROLLER_SELECTED_CLEAR:
      return {
        ...state,
        selected: null,
      };
    case CONTROLLERS_ADMIN_SET:
      return {
        ...state,
        fetching: false,
        adminList: action.controllers,
      };
    case MUTES_SET:
      return {
        ...state,
        mutes: {
          ...state.mutes,
          [action.controllerId]: action.mutes,
        },
      };
    case "LOGOUT":
      return defaultState;
    default:
      return state;
  }
}
